import React, { useState, useEffect } from 'react';
import {
  Phone, PhoneIncoming, PhoneOutgoing, PhoneMissed,
  Clock, User, Briefcase, FileText, Search,
  Play, Pause, Download, BarChart3, Brain, Sparkles,
  Mic, MicOff, TrendingUp, AlertTriangle, CheckCircle,
  X, ChevronRight, Wand2, ClipboardList
} from 'lucide-react';
import { voipService } from '../../services/voipService';
import { formatDateTime } from '../../utils/dateUtils';
import QuickIntakeModal from '../intake/QuickIntakeModal';

const CallLog = ({ extension }) => {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    direction: '',
    status: '',
    search: '',
    start_date: '',
    end_date: ''
  });
  const [selectedCall, setSelectedCall] = useState(null);
  const [playingRecording, setPlayingRecording] = useState(null);
  const [showAiInsights, setShowAiInsights] = useState(false);
  const [showQuickIntakeModal, setShowQuickIntakeModal] = useState(false);
  const [intakePreFill, setIntakePreFill] = useState(null);

  useEffect(() => {
    loadCalls();
  }, [filters]);

  const loadCalls = async () => {
    try {
      setLoading(true);
      const filterParams = {};
      if (filters.direction) filterParams.direction = filters.direction;
      if (filters.status) filterParams.status = filters.status;
      if (filters.start_date) filterParams.start_date = filters.start_date;
      if (filters.end_date) filterParams.end_date = filters.end_date;

      const data = await voipService.getCallLogs(filterParams);

      let filteredCalls = data;
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        filteredCalls = data.filter(call =>
          (call.from_number && call.from_number.includes(searchLower)) ||
          (call.to_number && call.to_number.includes(searchLower)) ||
          (call.from_name && call.from_name.toLowerCase().includes(searchLower)) ||
          (call.to_name && call.to_name.toLowerCase().includes(searchLower))
        );
      }

      setCalls(filteredCalls);
    } catch (error) {
      console.error('Error loading calls:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCallIcon = (call) => {
    if (call.direction === 'inbound') {
      return call.status === 'missed' ?
        <PhoneMissed className="w-5 h-5 text-red-400" /> :
        <PhoneIncoming className="w-5 h-5 text-green-400" />;
    } else if (call.direction === 'outbound') {
      return <PhoneOutgoing className="w-5 h-5 text-blue-400" />;
    }
    return <Phone className="w-5 h-5 text-cyan-400" />;
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      completed: 'bg-green-500/20 text-green-300 border-green-500/30',
      answered: 'bg-green-500/20 text-green-300 border-green-500/30',
      missed: 'bg-red-500/20 text-red-300 border-red-500/30',
      voicemail: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
      failed: 'bg-red-500/20 text-red-300 border-red-500/30',
      transferred: 'bg-blue-500/20 text-blue-300 border-blue-500/30'
    };

    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${statusStyles[status] || 'bg-slate-500/20 text-slate-300 border-slate-500/30'}`}>
        {status}
      </span>
    );
  };

  const getSentimentIcon = (sentiment) => {
    switch (sentiment) {
      case 'positive':
        return <TrendingUp className="w-4 h-4 text-green-400" />;
      case 'negative':
        return <AlertTriangle className="w-4 h-4 text-red-400" />;
      default:
        return <CheckCircle className="w-4 h-4 text-blue-400" />;
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleQuickIntake = (call) => {
    // Pre-fill intake with caller information
    const preFill = {};

    // Get the caller's number and name
    if (call.direction === 'inbound') {
      if (call.from_number) {
        preFill.primary_phone = call.from_number;
      }
      if (call.from_name && call.from_name !== 'Unknown') {
        // Try to split name into first/last
        const nameParts = call.from_name.trim().split(/\s+/);
        if (nameParts.length >= 2) {
          preFill.first_name = nameParts[0];
          preFill.last_name = nameParts.slice(1).join(' ');
        } else if (nameParts.length === 1) {
          preFill.first_name = nameParts[0];
        }
      }
    } else {
      if (call.to_number) {
        preFill.primary_phone = call.to_number;
      }
      if (call.to_name && call.to_name !== 'Unknown') {
        const nameParts = call.to_name.trim().split(/\s+/);
        if (nameParts.length >= 2) {
          preFill.first_name = nameParts[0];
          preFill.last_name = nameParts.slice(1).join(' ');
        } else if (nameParts.length === 1) {
          preFill.first_name = nameParts[0];
        }
      }
    }

    setIntakePreFill(preFill);
    setShowQuickIntakeModal(true);
  };

  // Calculate quick stats
  const stats = {
    total: calls.length,
    answered: calls.filter(c => c.status === 'answered' || c.status === 'completed').length,
    missed: calls.filter(c => c.status === 'missed').length,
    avgDuration: calls.length > 0
      ? Math.round(calls.reduce((acc, c) => acc + (c.duration_seconds || 0), 0) / calls.length)
      : 0
  };

  return (
    <div className="h-full flex bg-gradient-to-br from-slate-900 via-slate-900/30 to-slate-900 rounded-xl overflow-hidden">
      {/* Call List */}
      <div className="flex-1 flex flex-col">
        {/* Header with Stats */}
        <div className="p-4 border-b border-cyan-500/20">
          {/* Quick Stats */}
          <div className="grid grid-cols-4 gap-3 mb-4">
            <div className="bg-slate-800/50 backdrop-blur rounded-xl p-3 border border-cyan-500/20">
              <div className="text-2xl font-bold text-white">{stats.total}</div>
              <div className="text-xs text-cyan-300">Total Calls</div>
            </div>
            <div className="bg-slate-800/50 backdrop-blur rounded-xl p-3 border border-green-500/20">
              <div className="text-2xl font-bold text-green-400">{stats.answered}</div>
              <div className="text-xs text-green-300">Answered</div>
            </div>
            <div className="bg-slate-800/50 backdrop-blur rounded-xl p-3 border border-red-500/20">
              <div className="text-2xl font-bold text-red-400">{stats.missed}</div>
              <div className="text-xs text-red-300">Missed</div>
            </div>
            <div className="bg-slate-800/50 backdrop-blur rounded-xl p-3 border border-blue-500/20">
              <div className="text-2xl font-bold text-blue-400">{formatDuration(stats.avgDuration)}</div>
              <div className="text-xs text-blue-300">Avg Duration</div>
            </div>
          </div>

          {/* Filters */}
          <div className="flex gap-3 items-center flex-wrap">
            <div className="flex-1 min-w-64">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-cyan-400" />
                <input
                  type="text"
                  placeholder="Search by name or number..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-slate-800/50 border border-cyan-500/30 rounded-xl text-white placeholder-cyan-400/50 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                />
              </div>
            </div>

            <select
              value={filters.direction}
              onChange={(e) => handleFilterChange('direction', e.target.value)}
              className="px-4 py-2 bg-slate-800/50 border border-cyan-500/30 rounded-xl text-white focus:ring-2 focus:ring-cyan-500"
            >
              <option value="">All Directions</option>
              <option value="inbound">Inbound</option>
              <option value="outbound">Outbound</option>
              <option value="internal">Internal</option>
            </select>

            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="px-4 py-2 bg-slate-800/50 border border-cyan-500/30 rounded-xl text-white focus:ring-2 focus:ring-cyan-500"
            >
              <option value="">All Statuses</option>
              <option value="completed">Completed</option>
              <option value="answered">Answered</option>
              <option value="missed">Missed</option>
              <option value="voicemail">Voicemail</option>
            </select>

            <input
              type="date"
              value={filters.start_date}
              onChange={(e) => handleFilterChange('start_date', e.target.value)}
              className="px-4 py-2 bg-slate-800/50 border border-cyan-500/30 rounded-xl text-white focus:ring-2 focus:ring-cyan-500"
            />

            <button
              onClick={() => setFilters({ direction: '', status: '', search: '', start_date: '', end_date: '' })}
              className="px-4 py-2 text-sm text-cyan-300 hover:text-white transition-colors"
            >
              Clear
            </button>
          </div>
        </div>

        {/* Call List */}
        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-2 border-cyan-500 border-t-transparent"></div>
            </div>
          ) : calls.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <Phone className="w-12 h-12 text-cyan-500/50 mx-auto mb-3" />
                <p className="text-cyan-300">No calls found</p>
              </div>
            </div>
          ) : (
            <div className="divide-y divide-cyan-500/10">
              {calls.map((call) => (
                <div
                  key={call.id}
                  onClick={() => setSelectedCall(call)}
                  className={`p-4 hover:bg-cyan-500/10 cursor-pointer transition-all ${
                    selectedCall?.id === call.id ? 'bg-cyan-500/20 border-l-2 border-cyan-500' : ''
                  }`}
                >
                  <div className="flex items-center gap-4">
                    {/* Icon */}
                    <div className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${
                      call.status === 'missed' ? 'bg-red-500/20' :
                      call.direction === 'inbound' ? 'bg-green-500/20' :
                      'bg-blue-500/20'
                    }`}>
                      {getCallIcon(call)}
                    </div>

                    {/* Call Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-medium text-white truncate">
                          {call.direction === 'inbound'
                            ? (call.from_name || call.from_number || 'Unknown')
                            : (call.to_name || call.to_number || 'Unknown')
                          }
                        </p>
                        {getStatusBadge(call.status)}
                        {call.astraea_handled && (
                          <span className="px-2 py-0.5 bg-gradient-to-r from-cyan-500/30 to-blue-500/30 text-cyan-200 rounded-full text-xs font-medium border border-cyan-500/30 flex items-center gap-1">
                            <Sparkles className="w-3 h-3" />
                            AI
                          </span>
                        )}
                        {call.is_recorded && (
                          <Mic className="w-4 h-4 text-red-400" title="Recorded" />
                        )}
                      </div>
                      <p className="text-sm text-cyan-300">
                        {call.direction === 'inbound' ? call.from_number : call.to_number}
                      </p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-cyan-400">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDateTime(call.start_time, { year: undefined })}
                        </span>
                        {call.duration_seconds && (
                          <span>{formatDuration(call.duration_seconds)}</span>
                        )}
                        {call.contact_id && (
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            Linked
                          </span>
                        )}
                      </div>
                    </div>

                    <ChevronRight className="w-5 h-5 text-cyan-400" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Call Details Panel */}
      {selectedCall && (
        <div className="w-96 bg-slate-800/50 backdrop-blur border-l border-cyan-500/20 overflow-auto">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-white">Call Details</h3>
              <button
                onClick={() => setSelectedCall(null)}
                className="p-1 text-cyan-400 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Call Summary */}
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  selectedCall.status === 'missed' ? 'bg-red-500/20' :
                  selectedCall.direction === 'inbound' ? 'bg-green-500/20' :
                  'bg-blue-500/20'
                }`}>
                  {getCallIcon(selectedCall)}
                </div>
                <div>
                  <div className="font-medium text-white">
                    {selectedCall.direction === 'inbound'
                      ? (selectedCall.from_name || selectedCall.from_number || 'Unknown')
                      : (selectedCall.to_name || selectedCall.to_number || 'Unknown')
                    }
                  </div>
                  <div className="text-sm text-cyan-300">
                    {selectedCall.direction === 'inbound' ? selectedCall.from_number : selectedCall.to_number}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="bg-slate-900/50 rounded-lg p-3">
                  <div className="text-cyan-400 mb-1">Direction</div>
                  <div className="font-medium text-white capitalize">{selectedCall.direction}</div>
                </div>
                <div className="bg-slate-900/50 rounded-lg p-3">
                  <div className="text-cyan-400 mb-1">Status</div>
                  <div>{getStatusBadge(selectedCall.status)}</div>
                </div>
                <div className="bg-slate-900/50 rounded-lg p-3">
                  <div className="text-cyan-400 mb-1">Start Time</div>
                  <div className="font-medium text-white">
                    {formatDateTime(selectedCall.start_time)}
                  </div>
                </div>
                <div className="bg-slate-900/50 rounded-lg p-3">
                  <div className="text-cyan-400 mb-1">Duration</div>
                  <div className="font-medium text-white">{formatDuration(selectedCall.duration_seconds)}</div>
                </div>
              </div>
            </div>

            {/* Recording Player */}
            {selectedCall.is_recorded && (
              <div className="mb-6">
                <div className="text-sm font-medium text-cyan-200 mb-2 flex items-center gap-2">
                  <Mic className="w-4 h-4 text-red-400" />
                  Call Recording
                </div>
                <div className="bg-slate-900/50 rounded-xl p-4 border border-cyan-500/20">
                  <audio controls className="w-full">
                    <source src={`/api/voip/recordings/${selectedCall.recording_id}`} type="audio/wav" />
                  </audio>
                  <button className="mt-3 w-full px-4 py-2 bg-cyan-500/20 text-cyan-200 rounded-lg hover:bg-cyan-500/30 transition-colors flex items-center justify-center gap-2 text-sm">
                    <Download className="w-4 h-4" />
                    Download Recording
                  </button>
                </div>
              </div>
            )}

            {/* AI Analysis */}
            {selectedCall.astraea_handled && (
              <div className="mb-6">
                <div className="bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-lg flex items-center justify-center">
                      <Brain className="w-4 h-4 text-white" />
                    </div>
                    <span className="font-medium text-white">Astraea AI Analysis</span>
                  </div>
                  <p className="text-sm text-cyan-200 mb-3">
                    This call was handled by Astraea AI. View full interaction details for AI insights and actions taken.
                  </p>
                  <button className="text-sm font-medium text-cyan-300 hover:text-white transition-colors flex items-center gap-1">
                    View AI Interaction
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {/* Linked Records */}
            {(selectedCall.contact_id || selectedCall.case_id) && (
              <div className="mb-6">
                <div className="text-sm font-medium text-cyan-200 mb-2">Linked Records</div>
                <div className="space-y-2">
                  {selectedCall.contact_id && (
                    <a
                      href={`/contacts/${selectedCall.contact_id}`}
                      className="flex items-center gap-2 p-3 bg-blue-500/20 text-blue-200 rounded-xl hover:bg-blue-500/30 transition-colors border border-blue-500/30"
                    >
                      <User className="w-4 h-4" />
                      <span className="text-sm font-medium">View Contact</span>
                    </a>
                  )}
                  {selectedCall.case_id && (
                    <a
                      href={`/cases/${selectedCall.case_id}`}
                      className="flex items-center gap-2 p-3 bg-cyan-500/20 text-cyan-200 rounded-xl hover:bg-cyan-500/30 transition-colors border border-cyan-500/30"
                    >
                      <Briefcase className="w-4 h-4" />
                      <span className="text-sm font-medium">View Case</span>
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Notes */}
            <div className="mb-6">
              <div className="text-sm font-medium text-cyan-200 mb-2">Notes</div>
              <textarea
                value={selectedCall.notes || ''}
                placeholder="Add call notes..."
                className="w-full px-4 py-3 bg-slate-900/50 border border-cyan-500/30 rounded-xl text-white placeholder-cyan-400/50 focus:ring-2 focus:ring-cyan-500 resize-none"
                rows="4"
              />
            </div>

            {/* Quick Actions */}
            <div className="space-y-2">
              <button className="w-full px-4 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-medium hover:from-green-400 hover:to-emerald-500 transition-all flex items-center justify-center gap-2">
                <Phone className="w-4 h-4" />
                Call Back
              </button>
              <button
                onClick={() => handleQuickIntake(selectedCall)}
                className="w-full px-4 py-3 bg-gradient-to-r from-teal-500 to-cyan-600 text-white rounded-xl font-medium hover:from-teal-400 hover:to-cyan-500 transition-all flex items-center justify-center gap-2"
              >
                <ClipboardList className="w-4 h-4" />
                Quick Intake
              </button>
              <button className="w-full px-4 py-3 bg-slate-700/50 text-cyan-200 rounded-xl hover:bg-slate-600/50 transition-colors flex items-center justify-center gap-2">
                <Wand2 className="w-4 h-4" />
                Generate AI Summary
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quick Intake Modal */}
      <QuickIntakeModal
        isOpen={showQuickIntakeModal}
        onClose={() => {
          setShowQuickIntakeModal(false);
          setIntakePreFill(null);
        }}
        onSuccess={() => {
          setShowQuickIntakeModal(false);
          setIntakePreFill(null);
        }}
        preFill={intakePreFill}
      />
    </div>
  );
};

export default CallLog;
